import {
  ArrowLeftOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  ExpandAltOutlined,
  ShrinkOutlined,
} from '@ant-design/icons';
import { Waypoint } from '@omniafishing/core';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useCallback, useRef, useState } from 'react';
import { useOnClickOutside } from '../../../../hooks/use_on_click_outside';
import { useResponsive } from '../../../../hooks/use_responsive';
import { useUser } from '../../../../hooks/use_user';
import { getImgixPath } from '../../../../lib/imgix';
import { CurrentConditions } from '../../../current_conditions/current_conditions';
import { CurrentConditionsTimestamp } from '../../../current_conditions/current_conditions_timestamp';
import { ActiveMedia, MediaModal } from '../../../fishing_report_card/media_modal';
import ImageLoader from '../../../image_loader/image_loader';
import useImageLoader from '../../../image_loader/use_image_loader';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import SvgExpandIcon from '../../../svg/expand_icon';
import { ConditionsUpgrade } from './conditions_upgrade';
import { ShareWaypointButton } from './share_waypoint_button';
import { getWaypointDateToDisplay } from './waypoint_card';
import { WaypointDeleteModal } from './waypoint_delete_modal';
import styles from './waypoint_details_screen.less';
import { truncateTo6Decimal } from './waypoint_form_helper';
import { WaypointMoreButton } from './waypoint_more_button';

interface WaypointDetailsScreenProps {
  onClose: () => void;
  onEditableWaypointChange: (waypoint: Waypoint | null, flyTo?: boolean) => void;
  onSelectedWaypointChange: (waypoint: Waypoint | null) => void;
  onWaypointDelete: (waypointId: number) => void;
  showWaypointDetailsBackButton: boolean;
  waypoint: Waypoint;
}
export const WaypointDetailsScreen = React.memo((props: WaypointDetailsScreenProps) => {
  const {
    onClose,
    onEditableWaypointChange,
    onSelectedWaypointChange,
    onWaypointDelete,
    showWaypointDetailsBackButton,
    waypoint,
  } = props;
  const {
    name,
    waterbody,
    lat,
    lng,
    comments,
    public: isPublic,
    img,
    outing_at,
    conditions_saved,
    symbol_imported,
  } = waypoint;

  const { isPro } = useUser();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const { isMobile, isDesktop } = useResponsive();

  const containerRef = useRef<HTMLDivElement>(null);

  const { isImageLoading, isImageError, handleImageLoad, handleImageError } = useImageLoader();
  const [activeExpandedMedia, setActiveExpandedMedia] = useState<ActiveMedia>(null);
  const handleMediaClick = useCallback(
    (e: React.MouseEvent<HTMLImageElement | HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();

      setActiveExpandedMedia({
        src: getImgixPath(img, { w: 1000 }),
        type: 'img',
      });
    },
    [img]
  );

  const imagePath =
    img &&
    getImgixPath(img, {
      w: 225,
      crop: 'faces',
    });

  useOnClickOutside(
    containerRef,
    onClose,
    'mousedown',
    'touchstart',
    isDeleteModalOpen || !!activeExpandedMedia
  );

  const [mobileExpandedModal, setMobileExpandedModal] = useState(false);

  return (
    <div
      className={classNames(styles.modal, { [styles.modal__expanded]: mobileExpandedModal })}
      ref={containerRef}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.buttonAndHeader}>
            {showWaypointDetailsBackButton && (
              <span role="button" tabIndex={0} aria-label="Go back a screen">
                <ArrowLeftOutlined
                  onClick={() => {
                    onSelectedWaypointChange(null);
                  }}
                />
              </span>
            )}
            <div>
              <span className={styles.heading}>
                <span>{name}</span>
                {symbol_imported && (
                  <span className="text-[#657d7d] ml-1">({symbol_imported})</span>
                )}
              </span>
              <span className={styles.subHeading}>
                {isPublic
                  ? 'PUBLIC (you have shared this waypoint)'
                  : 'PRIVATE (only you can see this)'}{' '}
              </span>
            </div>
          </div>
          <div className="flex items-baseline">
            {isMobile && (
              <span
                role="button"
                tabIndex={1}
                className={styles.expandModalIcon}
                onClick={() => setMobileExpandedModal(!mobileExpandedModal)}
              >
                {mobileExpandedModal ? <ShrinkOutlined /> : <ExpandAltOutlined />}
              </span>
            )}
            <span role="button" tabIndex={isMobile ? 2 : 1} aria-label="close modal">
              <CloseOutlined onClick={onClose} />
            </span>
          </div>
        </div>
        <div className={styles.waypointDetails}>
          <div className="flex justify-between">
            <div className={styles.date}>
              <strong>Date/Time</strong>
              <span>
                <span>{getWaypointDateToDisplay(waypoint)}</span>
              </span>
            </div>
            {isMobile && (
              <div>
                <WaypointMoreButton
                  waypoint={waypoint}
                  onWaypointDelete={onWaypointDelete}
                  onEditableWaypointChange={onEditableWaypointChange}
                  buttonProps={{ size: 'md', shape: 'circle', kind: 'tertiary', fontSize: 19 }}
                />
              </div>
            )}
          </div>
          <div className={styles.location}>
            {waterbody && (
              <div className="flex-1">
                <strong>Waterbody</strong>
                <span>{waterbody.primary_name}</span>{' '}
              </div>
            )}
            <div className="flex-1">
              <strong>Latitude</strong>
              <span>{truncateTo6Decimal(lat)}°</span>
            </div>
            <div className="flex-1">
              <strong>Longitude</strong>
              <span>{truncateTo6Decimal(lng)}°</span>
            </div>
          </div>
          {comments && (
            <div className={styles.notes}>
              <strong>Notes</strong>
              <span>{comments}</span>
            </div>
          )}
          {img && (
            <>
              <div className="mb-3">
                <strong>Image</strong>
                <div className={styles.mediaContainer}>
                  <ImageLoader
                    className={classNames(styles.image)}
                    isImageError={isImageError}
                    isImageLoading={isImageLoading}
                    loadingClassName={styles.loading}
                    onClick={(e) => handleMediaClick(e)}
                    onError={handleImageError}
                    onLoad={(e) => {
                      handleImageLoad();
                    }}
                    src={imagePath}
                  />
                  <button className={styles.expandIcon} onClick={handleMediaClick}>
                    <SvgExpandIcon />
                  </button>
                </div>
              </div>
              <MediaModal
                activeMedia={activeExpandedMedia}
                onCancel={(e) => {
                  e.stopPropagation();
                  setActiveExpandedMedia(null);
                }}
              />
            </>
          )}
          <section className={styles.conditions}>
            <h3 className={styles.conditionsHeading}>
              Conditions at {dayjs(outing_at).format('MMM D, YYYY')},{' '}
              {dayjs(outing_at).format('h:mm A')}
            </h3>
            {conditions_saved ? (
              <CurrentConditions
                waypoint={waypoint}
                isProBlocked
                solunarLoading={false}
                weatherLoading={false}
              />
            ) : (
              <CurrentConditionsTimestamp lat={lat} lng={lng} timestamp={outing_at} isProBlocked />
            )}
            {!isPro && <ConditionsUpgrade />}
          </section>
          {isMobile && (
            <div className="flex mt-auto gap-3">
              <OmniaButton
                onPress={() => onEditableWaypointChange(waypoint)}
                kind="primary"
                size="md"
                block
                className="flex-1"
              >
                <EditOutlined /> Edit
              </OmniaButton>
              <ShareWaypointButton
                waypoint={waypoint}
                Button={
                  <OmniaButton kind="secondary" size="md" block className="flex-1">
                    Share
                  </OmniaButton>
                }
              />
              <OmniaButton
                onPress={() => setIsDeleteModalOpen(true)}
                kind="tertiary"
                size="md"
                block
                className="flex-1"
              >
                <DeleteOutlined /> Delete
              </OmniaButton>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {isDesktop && (
            <div className={styles.footerContent}>
              <div className={styles.buttonWrapper}>
                <OmniaButton
                  onPress={() => onEditableWaypointChange(waypoint)}
                  kind="primary"
                  size="lg"
                  shape="circle"
                  className={styles.actionButton}
                >
                  <EditOutlined />
                </OmniaButton>
                <strong>Edit</strong>
              </div>
              <div className={styles.buttonWrapper}>
                <ShareWaypointButton waypoint={waypoint} />
              </div>
              <div className={classNames(styles.buttonWrapper, 'ml-auto')}>
                <OmniaButton
                  onPress={() => setIsDeleteModalOpen(true)}
                  kind="tertiary"
                  size="lg"
                  shape="circle"
                  className={styles.actionButton}
                >
                  <DeleteOutlined />
                </OmniaButton>
                <strong>Delete</strong>
              </div>
            </div>
          )}
        </div>
      </div>
      <WaypointDeleteModal
        isOpen={isDeleteModalOpen}
        waypoint={waypoint}
        onOpenChange={setIsDeleteModalOpen}
        onWaypointDelete={onWaypointDelete}
      />
    </div>
  );
});
